/**
 * QualityGateDialog — Blocking quality assessment dialog (Phase 4)
 *
 * Shown after recording stops when the aggregate quality label is "fair" or "poor".
 * Presents friendly quality warnings and gives user the option to continue.
 *
 * Never hard-blocks — "Continue" is always available.
 *
 * See: docs/features/document-system/audio-quality-gates.md §3B
 */
import React, { useEffect } from "react";
import { AlertTriangle, XCircle } from "lucide-react";
import { QUALITY_POOR } from "../../utils/audioQualityUtils";

/**
 * @param {Object}   props
 * @param {boolean}  props.isOpen — Whether the dialog is visible
 * @param {string}   props.label — Quality label: "fair" | "poor"
 * @param {boolean}  props.blocked — If true, processing is blocked (word count too low)
 * @param {string}   props.blockReason — Explanation for block
 * @param {Object}   props.summary — Quality summary object from assessOverallQuality()
 * @param {Function} props.onProcessAnyway — User clicks "Continue"
 * @param {Function} props.onDiscard — User clicks "Discard" (kept for blocked case)
 */
const QualityGateDialog = ({
  isOpen,
  label,
  blocked = false,
  blockReason = "",
  summary = {},
  onProcessAnyway,
  onDiscard,
}) => {
  const isPoor = label === QUALITY_POOR;
  const confPercent = summary.avgConfidence ? Math.round(summary.avgConfidence * 100) : 0;
  const lowVolPercent = summary.lowVolumeRatio ? Math.round(summary.lowVolumeRatio * 100) : 0;
  const displayLabel = label ? label.charAt(0).toUpperCase() + label.slice(1) : "Unknown";

  // Build list of user-friendly issues
  const issues = [];
  if (summary.failedChunks > 0) {
    issues.push("We had trouble detecting speech in parts of your recording");
  }
  if (lowVolPercent > 0) {
    issues.push("Parts of your recording were quieter than expected");
  }

  // Log detailed quality info when dialog is shown (for debugging bug reports)
  useEffect(() => {
    if (isOpen) {
      console.warn(
        "[QualityGateDialog] Recording quality warning shown to user",
        {
          label,
          blocked,
          blockReason,
          avgConfidence: summary.avgConfidence,
          avgConfidencePercent: confPercent,
          failedChunks: summary.failedChunks,
          totalChunks: summary.totalChunks,
          lowVolumeRatio: summary.lowVolumeRatio,
          lowVolumePercent: lowVolPercent,
          wordCount: summary.wordCount,
          issues,
          timestamp: new Date().toISOString(),
        }
      );
    }
  }, [isOpen, label, blocked, blockReason, summary, confPercent, lowVolPercent, issues]);

  if (!isOpen) return null;

  return (
    <div className="modal" onClick={blocked ? undefined : onProcessAnyway}>
      <div className="modal-content quality-gate-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="quality-gate-header">
          {isPoor ? (
            <XCircle size={20} className="quality-gate-icon quality-gate-icon-poor" />
          ) : (
            <AlertTriangle size={20} className="quality-gate-icon quality-gate-icon-fair" />
          )}
          <h3 className="modal-title quality-gate-title">
            Recording Quality: {displayLabel}
          </h3>
        </div>

        {blocked ? (
          <div className="quality-gate-body">
            <p className="quality-gate-block-reason">{blockReason}</p>
          </div>
        ) : (
          <div className="quality-gate-body">
            <p className="quality-gate-description">
              Your recording may produce lower-quality results:
            </p>
            <ul className="quality-gate-metrics">
              {issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
              {issues.length === 0 && (
                <li>Audio quality was below our recommended threshold</li>
              )}
            </ul>
          </div>
        )}

        <div className="modal-button-container quality-gate-actions">
          {blocked ? (
            <button className="modal-cancel-button" onClick={onDiscard}>
              Discard
            </button>
          ) : (
            <button className="modal-confirm-button" onClick={onProcessAnyway}>
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QualityGateDialog;
